import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useTranslation } from 'react-i18next';
import { forwardRef, useEffect, useState } from 'react';

import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';

import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { useWarehouseRacks } from '@modules/WarehouseMap/hooks/useWarehouseRacks';
import { useWarehouses } from '@modules/WarehouseMap/hooks/useWarehouses';

export interface LocationFormValues {
  code: string;
  type_id: number | null;
  warehouse_id: number | null;
  rack_id: number | null;
  shelf: number | null;
  slot: number | null;
}

const typeMap: Record<string, number> = {
  Almacen: 1,
  Picking: 2,
};

export const locationFormSchema = z.object({
  code: z.string().min(2),
  type_id: z.number().nullable(),
  warehouse_id: z.number().nullable(),
  rack_id: z.number().nullable(),
  shelf: z
    .number()
    .nullable()
    .transform((val) => (val !== null && val >= 1 ? val : null)),
  slot: z
    .number()
    .nullable()
    .transform((val) => (val !== null && val >= 1 ? val : null)),
});

export type LocationFormSchema = z.infer<typeof locationFormSchema>;

interface LocationFormProps {
  defaultValues?: Partial<LocationFormValues>;
  disabled?: boolean;
  onCancel?: () => void;
  onSubmit?: (values: LocationFormValues) => void;
  submitLabel?: string;
  isSubmitting?: boolean;
}

const LocationForm = forwardRef<HTMLFormElement, LocationFormProps>(
  ({ defaultValues, disabled, onCancel, onSubmit, submitLabel, isSubmitting }, ref) => {
    const { t } = useTranslation('locationsModule');
    const warehousesQuery = useWarehouses();

    const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(defaultValues?.warehouse_id ?? null);

    const racksQuery = useWarehouseRacks(selectedWarehouse ?? 0);

    useEffect(() => {
      if (warehousesQuery.isLoading || racksQuery.isLoading) return;
    }, [warehousesQuery.isLoading, racksQuery.isLoading]);

    const form = useForm<LocationFormSchema>({
      resolver: zodResolver(locationFormSchema),
      defaultValues: {
        code: '',
        type_id: null,
        warehouse_id: selectedWarehouse,
        rack_id: null,
        shelf: null,
        slot: null,
        ...defaultValues,
      },
    });

    const {
      control,
      handleSubmit,
      reset,
      formState: { isValid },
    } = form;

    useEffect(() => {
      if (defaultValues) {
        reset({
          code: defaultValues.code ?? '',
          type_id: defaultValues.type_id ?? null,
          warehouse_id: selectedWarehouse,
          rack_id: defaultValues.rack_id ?? null,
          shelf: defaultValues.shelf ?? null,
          slot: defaultValues.slot ?? null,
        });
      }
    }, [defaultValues]);

    const racks = selectedWarehouse ? (racksQuery.data ?? []) : [];

    const handleSubmitForm = handleSubmit((values) => {
      onSubmit?.(values);
    });

    return (
      <form
        ref={ref}
        className="space-y-4 flex flex-col justify-between h-full"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitForm();
        }}
      >
        <div className="space-y-4">
          {/* Code */}
          <div>
            <Label htmlFor="location-code" className="text-sm font-medium text-text">
              {t('modal.create.code_label')} *
            </Label>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <Input
                  id="location-code"
                  {...field}
                  placeholder={t('modal.create.code_placeholder')}
                  disabled={disabled}
                  autoFocus
                />
              )}
            />
          </div>

          {/* Type */}
          <div>
            <Label htmlFor="location-type" className="text-sm font-medium text-text">
              {t('modal.create.type_label')} *
            </Label>
            <Controller
              name="type_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ? Object.keys(typeMap).find((key) => typeMap[key] === field.value) || '' : ''}
                  onValueChange={(value) => field.onChange(typeMap[value])}
                  disabled={disabled}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder={t('modal.create.type_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Almacen">{t('modal.create.type_warehouse')}</SelectItem>
                    <SelectItem value="Picking">{t('modal.create.type_picking')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Warehouse */}
          <div>
            <Label htmlFor="location-warehouse" className="text-sm font-medium text-text">
              {t('modal.create.warehouse_label')} *
            </Label>
            <Controller
              name="warehouse_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={String(field.value ?? '')}
                  onValueChange={(value) => {
                    const newId = Number(value);
                    setSelectedWarehouse(newId);
                    field.onChange(newId);
                  }}
                  disabled={disabled || warehousesQuery.isLoading}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder={t('modal.create.warehouse_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {warehousesQuery.data?.map((wh) => (
                      <SelectItem key={wh.id} value={String(wh.id)}>
                        {wh.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Shelf */}
          <div>
            <Label htmlFor="location-shelf" className="text-sm font-medium text-text">
              {t('modal.create.shelf_label')}
            </Label>
            <Controller
              name="shelf"
              control={control}
              render={({ field }) => (
                <Input
                  id="location-shelf"
                  type="number"
                  min={0}
                  placeholder={t('modal.create.shelf_placeholder')}
                  value={field.value === null ? '' : field.value}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                  disabled={disabled}
                />
              )}
            />
          </div>

          {/* Slot */}
          <div>
            <Label htmlFor="location-slot" className="text-sm font-medium text-text">
              {t('modal.create.slot_label')}
            </Label>
            <Controller
              name="slot"
              control={control}
              render={({ field }) => (
                <Input
                  id="location-slot"
                  type="number"
                  min={0}
                  placeholder={t('modal.create.slot_placeholder')}
                  value={field.value === null ? '' : field.value}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                  disabled={disabled}
                />
              )}
            />
          </div>

          {/* Rack */}
          <div>
            <Label htmlFor="location-rack" className="text-sm font-medium text-text">
              {t('modal.select_rack.title')}
            </Label>

            {!selectedWarehouse ? (
              <p className="mt-1 text-sm text-muted-foreground">{t('modal.select_rack.no_warehouse')}</p>
            ) : (
              <Controller
                name="rack_id"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={racks.map((r) => ({ label: r.code, value: String(r.id) }))}
                    placeholder="Selecciona un rack"
                    value={field.value ? String(field.value) : ''}
                    setValue={(v) => field.onChange(v === '' ? null : Number(v))}
                    disabled={disabled}
                  />
                )}
              />
            )}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset();
              onCancel?.();
            }}
            name="cancel"
            size="lg"
            disabled={disabled}
          >
            {t('button.cancel')}
          </Button>
          <Button variant="primary" name="confirm" size="lg" disabled={disabled || !isValid}>
            {isSubmitting ? t('button.creating') : (submitLabel ?? t('button.create'))}
          </Button>
        </div>
      </form>
    );
  },
);

LocationForm.displayName = 'LocationForm';

export default LocationForm;
